/* =====================================================================================
   Gift Builder V4: page engine. Same engine as Gift Builder V2 rev 32 (cards, gift box, cart, phone bar),
   used by sections/gift-builder-v4.liquid.

   V4: the page reads like a collection page. Steps are listed on the left (tabs on phones) with the gift box
   under them; only the picked step shows on the right (see bindV4Steps()). There is no step accordion, no
   auto move to the next step, no inner scroll box and no "Viewing x of y" counter. A "pick a collection"
   step shows big photo tiles; once one is picked, a switch in the step header changes collection.
   Everything V4-only runs when #gift-builder-v2 has the class gb-page--v4.
   V4 rev 2: an add that completes the step moves on to the next unfinished step by itself (see v4AutoAdvance());
   each step header has a Reset; the left column is compact and stays on screen while scrolling (CSS).

   Gift Builder V2 history (unchanged engine):

   Rev 30 (phones): "How it works" stacks; each step's products scroll sideways with arrows; the bottom bar lists
   what is picked in each step and opens the gift box; the theme's back-to-top button is hidden on this page.
   Rev 30: once a collection is picked, the other tiles fade back and a heading names the group above its products.
   Rev 29: collection tiles are never pre-picked (no memory across refreshes).
   Rev 27: "Reset" only appears once a product from that step is in the gift box (not for a picked collection alone).
   Rev 24: the "pick a collection" step takes up to 8 collections in one row with left/right arrows.
   Rev 23: a step can let the shopper pick ONE collection first (tiles with images; products appear once one is
   picked, see bindChoices()); each step header has a "Reset" that clears that step's picks (bindStepResets());
   the checklist drops its number boxes ("Step 1 Gisele PJ ... 1 added ✓"); on desktop a card's photos cycle
   on hover like collection page cards (bindCardSlides()).
   Rev 22: monogrammed PJs and their fee lines carry the monogram app's own link (_pc_pricing_ref), so the
   cart hides the fee line, keeps its quantity matched and removes it with the PJ, as on product pages.
   Rev 21: a "Ready to gift ✓" tag shows next to "My Gift Box" once every rule is met (see updateUI()).
   Rev 20: no jump on page load: the scroll box, its "Viewing 1–8 of 12" line and the gift box checklist
   now come with the page itself, so the JS only fills them in (see bindGridWindow()).
   Rev 19: a step closes by itself once its minimum is met and the page moves on to the next step
   (see autoAdvance()); once a product is added one way ("Add to Gift" or "Add with Monogram") its
   other button is greyed out; "Add to Gift with Monogram" is now "Add with Monogram"; the gift box
   tick is saved to the cart BEFORE the items are added and checked again after (see addToCart()),
   so another script updating the cart at the same moment can't leave the cart's gift box unticked;
   the gift box checklist shows each step's number (like the strip above Step 1); the rules strip
   stays pinned under the site header while scrolling (see bindStickyRules()).
   Rev 17: "Gift extras": the gift note is a checkbox like the gift box; ticking it opens the message
   field (unticked = no note sent). One message line under the button replaces the progress bar text.
   Rev 16: each free extra has its own discount code (set next to its "free" tick); the codes are
   put on the cart directly, so two different codes can both apply. Checklist total reads "4 added".
   Rev 15: set up from the theme editor: any number of steps, each with its own minimum and
   maximum (plus a minimum for the whole gift); the rules show above Step 1, next to each step
   title and as a checklist in the gift box; monogramming and the gift box can each be free or
   paid. Paid extras show their price and count in the subtotal; the discount code applied at
   checkout matches what is free (see applyDiscountCodes()).
   Rev 14: the scroll window now applies to every step (Step 1 and Step 2), not only Step 1.
   Rev 13: on desktop, Step 1's products scroll inside a window that fits the screen, with an
   always-visible scrollbar and a "Viewing 1–8 of 40" counter (see bindGridWindow()).
   Rev 12: every monogrammed PJ now carries a "Preview" image link in the cart and order, like
   product-page monogram orders. The picture is drawn on the shopper's own device (photo + letters
   placed using the product-page monogram app's own settings), then uploaded with the cart add.
   Drawn as soon as the monogram is saved, so "Add Gift to Cart" stays fast. If a picture can't be
   made, the PJ is still added (just without the link) - checkout is never blocked.
   Rev 11: held items re-read their price on every page load, so switching country updates the box;
   non-US "$" prices (CAD, AUD...) show their currency code, e.g. "$300.00 CAD" (formatMoney()).
   Rev 10: items land in the cart in the same order as the gift box (see buildCartLines()).
   Rev 9: the gift note and the gift box tick are saved with the held items, so a page refresh
   no longer silently drops the note (the items came back but the note did not). At "Add Gift to
   Cart" the note is also read straight from the note boxes, so it is sent even if typing never
   fired an update (autofill, dictation, browser-restored text).
   Rev 8: "Add Gift to Cart" also sets the cart attributes that tick the cart's gift box, and saves
   the note and those attributes in one update (see addToCart()).
   Rev 7: steps never auto-collapse; only the shopper opens/closes them (header click). (Replaced in rev 19.)
   Rev 6: removing items that leave a step incomplete reopens that step, scrolls to it and
   flashes its header (see reopenStepIfIncomplete()).
   Rev 2 changes: once an item is in the gift box, the button that added it changes to
   "Added to Gift ✓" or "Added to Gift with Monogram ✓" (see refreshCardButtons()).

   How it works now:
   - Each card shows its sizes on the photo. The shopper picks a size, then "Add to Gift" or
     "Add to Gift with Monogram".
   - Picks are HELD on this page in `this.heldItems` (nothing touches the cart). They are also saved
     in sessionStorage so a page refresh does not empty the gift box.
   - "Add Gift to Cart" sends ONE /cart/add.js request with every product, a $15 "Monogramming"
     line per monogrammed PJ, and the gift box (if ticked). Then it saves the gift note and
     puts the discount codes for the free extras on the cart (rev 16), so they ring up free.
   - Monogram details are written with the SAME property names real monogram orders use
     ("Single Line Text", "Choose Your Font Style", the final-sale acknowledgment, "LPROP").
   - The site's Quick View drawer is no longer used on this page.
   ===================================================================================== */
class GiftBuilderV2 {
  constructor() {
    this.page = document.getElementById('gift-builder-v2');
    if (!this.page) return; // not on the gift builder page: do nothing

    // Settings passed in from the section file (data-* attributes on #gift-builder-v2)
    this.minItems = parseInt(this.page.dataset.minItems) || 3;
    this.giftBoxVariantId = this.page.dataset.giftBoxVariant;
    this.monogramAddonVariantId = this.page.dataset.monogramAddonVariant;

    // Rev 15: which extras are free here, their prices when not, and the matching discount codes
    const d = this.page.dataset;
    this.giftBoxFree = d.giftBoxFree !== 'false';                        // anything but "false" = free (old pages)
    this.monogramFree = d.monogramFree !== 'false';
    this.giftBoxPrice = parseInt(d.giftBoxPrice) || 0;                   // cents, shopper's currency
    this.monogramPrice = parseInt(d.monogramPrice) || 0;
    // Rev 16: one code per free extra (older markup only had the shared ComplimentaryGift code)
    this.giftBoxCode = (d.giftBoxCode ?? d.compDiscountCode ?? '').trim();
    this.monogramCode = (d.monogramCode ?? d.compDiscountCode ?? '').trim();

    // Items held in the gift box. Each entry:
    // { uid, productId, variantId, step, title, color, size, price, image, monogram: null | { text, font } }
    this.heldItems = [];
    this.storageKey = 'gb-v2-held-items';

    // Chosen size per product card: { [productId]: { variantId, sizeTitle, sizeLabel } }
    // sizeLabel is the short text on the size button (e.g. "M"), used in "Add to Gift · Size M".
    this.pickedSize = {};

    // Monogram popup state
    this.activeMonoProductId = null;
    this.monoFont = 'italic';

    this.giftBoxChecked = d.giftBoxDefault !== 'false';                  // rev 15: starting tick set in the editor
    this.giftNote = '';

    // Rev 12: monogram preview pictures, kept in memory: { [item uid]: Promise<Blob|null> }
    this.previewJobs = {};
    this.placementCache = {};   // monogram placement per product, fetched once: { [productId]: Promise }
    this.fontCache = {};        // monogram fonts, loaded once: { Italic: Promise, Typewriter: Promise }

    // Step accordion: the shopper can open/close each step via its header; rev 19 also closes a step once its minimum is met.
    // Rev 15: each step's rules come from the section: min (0 = optional), max (0 = no limit), item names.
    this.stepState = {};
    this.page.querySelectorAll('.gb-step[data-min-for-step]').forEach(section => {
      const stepNum = section.id.replace('gb-step-', '');
      const min = parseInt(section.dataset.minForStep);
      this.stepState[stepNum] = {
        open: true,
        autoCollapsed: false,
        minForStep: isNaN(min) ? 1 : min,                              // pages without the setting: 1, as before
        maxForStep: parseInt(section.dataset.maxForStep) || 0,
        one: section.dataset.itemOne || 'item',                        // "Gisele PJ"
        many: section.dataset.itemMany || 'items',                     // "Gisele PJs"
      };
    });
    // Step minimums are enforced only with the rev 15 section (older markup: just the whole-gift minimum, as before)
    this.stepRules = !!this.page.querySelector('.gb-step[data-max-for-step]');
    this.v4 = this.page.classList.contains('gb-page--v4');            // V4: step list + one step at a time
    // The whole gift needs at least this many items, and never fewer than the steps' minimums added up
    const stepMins = Object.values(this.stepState).reduce((s, st) => s + st.minForStep, 0);
    if (this.stepRules) this.minItems = Math.max(this.minItems, stepMins);

    this.restoreHeldItems();      // bring back picks from before a refresh
    this.bindCardEvents();        // size buttons + Add to Gift buttons
    this.bindMonogramModalEvents();
    this.bindStepAccordionEvents();
    this.bindTabEvents();
    this.bindChoices();           // rev 23: "pick a collection first" steps
    this.bindCardSlides();        // rev 23: photos cycle on hover, like collection page cards
    this.bindStepResets();        // rev 23: Reset in each step header
    this.bindColsScrollEvents();
    this.bindSidebarEvents();
    this.bindMobileEvents();
    this.bindGiftBoxEvents();
    this.bindGiftMessageEvents();
    if (!this.v4) this.bindGridWindow();   // rev 13/14: scroll window + counter (V4: a normal page grid instead)
    this.bindStickyRules();       // rev 19: pinned rules strip; steps pin just below it
    this.bindGridCarousels();     // rev 30: phones: each step's products in a sideways carousel with arrows
    if (this.v4) this.bindV4Steps();   // V4: step list / phone tabs
    this.updateUI();
  }

  /* ---- SAVE / RESTORE HELD ITEMS (survives a refresh in the same tab) ---- */
  saveHeldItems() {
    try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.heldItems)); } catch (e) { /* storage blocked: the box still works, it just won't survive a refresh */ }
  }

  restoreHeldItems() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
      // Only keep items whose card is still on the page (a product may have been removed since)
      this.heldItems = Array.isArray(saved)
        ? saved.filter(i => this.page.querySelector(`.gb-card[data-product-id="${i.productId}"]`))
        : [];
      // Rev 11: re-read each price from its card on this page load, so the box always uses the
      // shopper's current currency (e.g. picks made in USD, then the country is switched to the UK).
      this.heldItems.forEach(i => {
        const card = this.page.querySelector(`.gb-card[data-product-id="${i.productId}"]`);
        const price = parseInt(card.dataset.productPrice);
        if (!isNaN(price)) i.price = price;
      });
    } catch (e) {
      this.heldItems = [];
    }
  }

  /* ---- CARD EVENTS: one listener for every card on the page (both steps) ---- */
  bindCardEvents() {
    this.page.addEventListener('click', e => {
      // 1) A size button on a photo
      const sizeBtn = e.target.closest('[data-gb-size]');
      if (sizeBtn && !sizeBtn.disabled) {
        const card = sizeBtn.closest('.gb-card');
        this.selectSize(card, sizeBtn);
        return;
      }

      // 2) "Add to Gift"
      // Buttons already showing "Added..." do nothing (pick another size to add again)
      const addBtn = e.target.closest('[data-gb-add]');
      if (addBtn && !addBtn.disabled && !addBtn.classList.contains('is-added')) {
        this.addHeldItem(addBtn.closest('.gb-card'), null);
        return;
      }

      // 3) "Add to Gift with Monogram": open the popup first
      const monoBtn = e.target.closest('[data-gb-add-mono]');
      if (monoBtn && !monoBtn.disabled && !monoBtn.classList.contains('is-added')) {
        this.openMonogramModal(monoBtn.closest('.gb-card'));
      }
    });

    // Remember which cards were rendered as fully sold out, so their "Sold Out" button never changes
    this.page.querySelectorAll('.gb-card').forEach(card => {
      const btn = card.querySelector('[data-gb-add]');
      if (btn && btn.textContent.trim() === 'Sold Out') card.dataset.gbSoldOut = '1';
    });

    // Products without sizes (one variant) are ready to add straight away
    this.page.querySelectorAll('.gb-card[data-single-variant]:not([data-gb-sold-out])').forEach(card => {
      this.pickedSize[card.dataset.productId] = { variantId: card.dataset.singleVariant, sizeTitle: '', sizeLabel: '' };
    });
  }

  // Highlights the chosen size; the buttons are then relabelled by refreshCardButtons()
  selectSize(card, sizeBtn) {
    const productId = card.dataset.productId;
    card.querySelectorAll('[data-gb-size]').forEach(b => b.setAttribute('aria-pressed', String(b === sizeBtn)));
    this.pickedSize[productId] = {
      variantId: sizeBtn.dataset.variantId,
      sizeTitle: sizeBtn.dataset.sizeTitle,
      sizeLabel: sizeBtn.textContent.trim(),
    };
    this.refreshCardButtons(card);
  }

  /* Sets both buttons on one card from the current state:
     - this size (or this one-size product) is already in the box without a monogram -> "Added to Gift ✓"
     - ...with a monogram -> "Added to Gift with Monogram ✓"
     - otherwise -> "Add to Gift · M" / "Add to Gift with Monogram" (or "Select a Size" if none picked)
     Choosing a different size resets the buttons so another size can be added. */
  refreshCardButtons(card) {
    const productId = card.dataset.productId;
    const pick = this.pickedSize[productId];
    const addBtn = card.querySelector('[data-gb-add]');
    const monoBtn = card.querySelector('[data-gb-add-mono]');
    if (card.dataset.gbSoldOut === '1') return; // sold-out card: leave as rendered

    // Items in the box for this product AND the currently chosen size
    const sameSize = this.heldItems.filter(i => i.productId === productId && (!pick || i.variantId === pick.variantId));
    const plainAdded = !!pick && sameSize.some(i => !i.monogram);
    const monoAdded = !!pick && sameSize.some(i => i.monogram);
    // Rev 15: this card's step already holds its maximum, so nothing more can be added from it
    const full = this.isStepFull(card.dataset.step);

    // Rev 19: which way this product (any size) is already in the box; the other way is then greyed out
    const hasPlain = this.heldItems.some(i => i.productId === productId && !i.monogram);
    const hasMono = this.heldItems.some(i => i.productId === productId && i.monogram);

    if (addBtn) {
      addBtn.disabled = !pick || (full && !plainAdded) || (hasMono && !plainAdded);   // rev 19: already added with a monogram
      addBtn.classList.toggle('is-added', plainAdded);
      addBtn.setAttribute('aria-disabled', String(plainAdded || addBtn.disabled));
      addBtn.textContent = !pick ? 'Select a Size'
        : plainAdded ? 'Added to Gift ✓'
        : full ? 'Step Limit Reached'
        : (pick.sizeLabel ? `Add to Gift · Size ${pick.sizeLabel}` : 'Add to Gift');
    }
    if (monoBtn) {
      // Rev 19: once this product is in the box WITHOUT a monogram, its monogram button is greyed out
      monoBtn.disabled = !pick || (full && !monoAdded) || (hasPlain && !monoAdded);
      monoBtn.classList.toggle('is-added', monoAdded);
      monoBtn.setAttribute('aria-disabled', String(monoAdded || monoBtn.disabled));
      monoBtn.textContent = monoAdded ? 'Added with Monogram ✓' : 'Add with Monogram';   // rev 19: shorter label
    }
  }

  // Puts one item in the gift box (held, not in the cart)
  addHeldItem(card, monogram) {
    const productId = card.dataset.productId;
    const pick = this.pickedSize[productId];
    if (!pick) return; // no size chosen yet
    if (this.isStepFull(card.dataset.step)) return; // rev 15: step already at its maximum
    // Rev 19: a product goes in one way only (plain OR with a monogram)
    if (this.heldItems.some(i => i.productId === productId && !!i.monogram !== !!monogram)) return;

    this.heldItems.push({
      uid: `${productId}-${Date.now()}`,           // unique id so the remove (×) button removes the right line
      productId,
      variantId: pick.variantId,
      step: card.dataset.step,
      title: card.dataset.productTitle,
      color: card.dataset.productColor || '',
      size: pick.sizeTitle,
      price: parseInt(card.dataset.productPrice) || 0,
      image: card.dataset.productImage,
      monogram,                                     // null, or { text, font }
    });
    this.saveHeldItems();
    this.updateUI();
    this.autoAdvance(card.dataset.step);           // rev 19: close a finished step, move to the next
    // Rev 12: start drawing the monogram preview now, in the background, so it's ready at checkout
    const added = this.heldItems[this.heldItems.length - 1];
    if (monogram) this.startPreview(added);
  }

  /* Rev 19: the moment a step reaches its minimum (e.g. its 1st Gisele PJ), it closes and the page moves to
     the next step that still needs something (opening it if needed). Only on the add that completes the
     step: optional steps (minimum 0) never close by themselves, and a step the shopper reopens to add
     more stays open. Short pause first so the shopper sees "Added to Gift ✓". */
  autoAdvance(stepNum) {
    if (this.v4) { this.v4AutoAdvance(stepNum); return; }   // V4 rev 2: move on to the next unfinished step
    const state = this.stepState[stepNum];
    if (!state || state.minForStep < 1 || this.stepCount(stepNum) !== state.minForStep) return;
    const order = Object.keys(this.stepState);
    const next = order.slice(order.indexOf(String(stepNum)) + 1).find(n => !this.isStepDone(n))
      || order.find(n => n !== String(stepNum) && !this.isStepDone(n));   // an earlier step still short
    clearTimeout(this.advanceTimer);
    this.advanceTimer = setTimeout(() => {
      if (this.stepCount(stepNum) < state.minForStep) return;   // an item was removed meanwhile: leave the step open
      state.autoCollapsed = true;
      this.toggleStep(stepNum, false);
      if (!next) return;                         // everything done: stay put (the gift box says "Ready to gift")
      this.toggleStep(next, true);
      const section = document.getElementById(`gb-step-${next}`);
      if (section) setTimeout(() => section.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);   // after the close settles
    }, 700);
  }

  removeHeldItem(uid) {
    clearTimeout(this.advanceTimer);                 // rev 19: a pending auto-close no longer applies
    const removed = this.heldItems.find(i => i.uid === uid);
    this.heldItems = this.heldItems.filter(i => i.uid !== uid);
    delete this.previewJobs[uid];                    // rev 12: drop its preview picture too
    this.saveHeldItems();
    this.updateUI();
    // If that removal left its step short of what it needs, take the shopper back to that step
    if (removed) this.reopenStepIfIncomplete(removed.step);
  }

  /* Rev 6: when items are removed from the gift box and a step drops below its required count
     (e.g. every Step 1 PJ was removed), that step is reopened, the page scrolls back to it, and its
     header flashes soft rose so it is clear where to pick again. The empty gift-box icon in the
     sidebar comes back on its own once the box is empty (toggleEmptyStates). */
  reopenStepIfIncomplete(stepNum) {
    if (this.v4) return;                         // V4: no jump; the step list shows the step is short again
    const state = this.stepState[stepNum];
    if (!state) return;
    const count = this.heldItems.filter(i => i.step === stepNum).length;
    if (count >= state.minForStep) return;      // step still complete: stay where the shopper is

    state.autoCollapsed = false;                 // (kept for compatibility; steps no longer auto-collapse)
    this.toggleStep(stepNum, true);              // open the step
    if (document.getElementById('gb-drawer')?.classList.contains('gb-drawer--open')) this.closeDrawer(); // phones: close the gift box drawer first

    const section = document.getElementById(`gb-step-${stepNum}`);
    if (!section) return;
    setTimeout(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      section.classList.remove('gb-step--attention');
      void section.offsetWidth;                  // restart the flash if it was already running
      section.classList.add('gb-step--attention');
      setTimeout(() => section.classList.remove('gb-step--attention'), 2000);
    }, 350);                                     // wait for the step to finish opening
  }

  /* ---- MONOGRAM POPUP ----
     Reuses the existing popup markup in the section file (#gb-monogram-modal). This code also
     updates its wording: the +$15.00 tag shows only when monogramming is paid (rev 15), the product line
     shows colour + size, and the save button reads "Add to Gift with Monogram". */
  bindMonogramModalEvents() {
    this.overlay = document.getElementById('gb-monogram-overlay');
    this.modal = document.getElementById('gb-monogram-modal');
    this.monoInput = document.getElementById('gb-monogram-input');
    this.monoCounter = document.getElementById('gb-monogram-counter');
    this.monoAck = document.getElementById('gb-monogram-ack');
    this.monoSaveBtn = document.getElementById('gb-monogram-save');
    this.monoProductTitle = document.getElementById('gb-monogram-product-title');
    if (!this.modal) return;

    // Wording changes on the existing popup
    const priceTag = this.modal.querySelector('.gb-monogram-modal__price');
    if (priceTag) {
      // Rev 15: free here = no price tag; paid = "+$15.00" in the shopper's currency
      priceTag.hidden = this.monogramFree;
      if (!this.monogramFree) priceTag.textContent = `+${this.formatMoney(this.monogramPrice)}`;
    }
    this.monoSaveBtn.textContent = 'Add with Monogram';   // rev 19: shorter label

    document.getElementById('gb-monogram-close')?.addEventListener('click', () => this.closeMonogramModal());
    document.getElementById('gb-monogram-cancel')?.addEventListener('click', () => this.closeMonogramModal());
    this.overlay?.addEventListener('click', () => this.closeMonogramModal());
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !this.modal.hidden) this.closeMonogramModal(); });

    // Letters and spaces only, max 6, shown in capitals (same rule as before)
    this.monoInput.addEventListener('input', () => {
      const clean = this.monoInput.value.replace(/[^a-zA-Z ]/g, '').slice(0, 6).toUpperCase();
      if (clean !== this.monoInput.value) this.monoInput.value = clean;
      const left = 6 - this.monoInput.value.length;
      this.monoCounter.textContent = `${left} character${left === 1 ? '' : 's'} left`;
      this.updateMonogramSaveState();
    });
    this.monoAck.addEventListener('change', () => this.updateMonogramSaveState());

    // Italic / Typewriter choice
    this.modal.querySelectorAll('[data-gb-monogram-font-btn]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.modal.querySelectorAll('[data-gb-monogram-font-btn]').forEach(b => b.classList.remove('gb-monogram-modal__font-btn--active'));
        btn.classList.add('gb-monogram-modal__font-btn--active');
        this.monoFont = btn.dataset.font;
      });
    });

    this.monoSaveBtn.addEventListener('click', () => this.saveMonogram());
  }

  openMonogramModal(card) {
    const productId = card.dataset.productId;
    const pick = this.pickedSize[productId];
    if (!pick) return;
    this.activeMonoProductId = productId;

    // e.g. "Gisele TENCEL™ Modal Long PJ Set · Woodrose/Ivory · Medium"
    this.monoProductTitle.textContent = [card.dataset.productTitle, card.dataset.productColor, pick.sizeTitle].filter(Boolean).join(' · ');

    // Start fresh every time
    this.monoInput.value = '';
    this.monoFont = 'italic';
    this.monoAck.checked = false;
    this.modal.querySelectorAll('[data-gb-monogram-font-btn]').forEach(b => {
      b.classList.toggle('gb-monogram-modal__font-btn--active', b.dataset.font === 'italic');
    });
    this.monoCounter.textContent = '6 characters left';
    this.updateMonogramSaveState();

    this.modal.hidden = false;
    this.overlay.hidden = false;
    this.monoInput.focus();
  }

  closeMonogramModal() {
    this.modal.hidden = true;
    this.overlay.hidden = true;
    this.activeMonoProductId = null;
  }

  // Save button only works once there are letters AND the final-sale box is ticked
  updateMonogramSaveState() {
    this.monoSaveBtn.disabled = !(this.monoInput.value.trim().length > 0 && this.monoAck.checked);
  }

  saveMonogram() {
    const productId = this.activeMonoProductId;
    const text = this.monoInput.value.trim();
    if (!productId || !text || !this.monoAck.checked) return;
    const card = this.page.querySelector(`.gb-card[data-product-id="${productId}"]`);
    this.closeMonogramModal();
    if (card) this.addHeldItem(card, { text, font: this.monoFont === 'typewriter' ? 'Typewriter' : 'Italic' });
  }

  /* ---- MONOGRAM PREVIEW PICTURE (rev 12) ----
     Product-page monogram orders carry a "Preview" property: a link to a picture of the PJ with the
     letters on the pocket. That picture is made by the monogram app (Zepto Product Personalizer)
     in the shopper's browser and uploaded with the cart add. This does the same here:
     1) read the app's placement for this product (photo, position, angle, size, thread colour),
     2) draw photo + letters on a 400px-wide canvas in the shopper's chosen font,
     3) attach the picture to the PJ's cart line; Shopify stores it and saves the link on the order. */

  // Starts (or reuses) the picture for one held item. Never throws: resolves to a Blob or null.
  startPreview(item) {
    if (!item || !item.monogram) return Promise.resolve(null);
    if (!this.previewJobs[item.uid]) {
      this.previewJobs[item.uid] = this.drawPreview(item).catch(err => {
        console.warn('Gift builder: monogram preview skipped', err);
        return null;                                // no picture: the PJ is still added normally
      });
    }
    return this.previewJobs[item.uid];
  }

  // The monogram app's settings for one product, read from the app's own per-product script.
  // Same file the product page loads; fetched once per product per visit (about 40-100ms).
  getPlacement(productId) {
    if (!this.placementCache[productId]) {
      const shop = (window.Shopify && window.Shopify.shop) || 'eberjey-store.myshopify.com';
      const url = `https://cdn-zeptoapps.com/product-personalizer/canvas-script.php?shop=${encodeURIComponent(shop)}&prid=${encodeURIComponent(productId)}`;
      this.placementCache[productId] = fetch(url)
        .then(r => { if (!r.ok) throw new Error(`placement ${r.status}`); return r.text(); })
        .then(text => {
          // Settings are one comma list stored as "cstmfy_meta_1"; we only need the first 19 values
          const m = text.match(/cstmfy_meta_1\\?"\s*:\s*\\?"(.*?)\\?"/);
          if (!m) throw new Error('no monogram settings for this product');
          const f = m[1].replace(/\\\//g, '/').split(',');
          const place = {
            x: parseFloat(f[2]), y: parseFloat(f[3]),         // box position, % of photo width / height
            w: parseFloat(f[4]), h: parseFloat(f[14]),        // box width % of width, height % of height
            rot: parseFloat(f[11]) || 0,                      // angle in degrees (e.g. 338 = tilted 22°)
            color: /^#[0-9a-f]{3,8}$/i.test(f[6]) ? f[6] : '#F3ECE0', // thread colour (ivory)
            image: (f[18] || '').trim(),                      // the exact photo the app draws on
          };
          if ([place.x, place.y, place.w, place.h].some(isNaN) || !/^https:\/\/cdn\.shopify\.com\//.test(place.image)) {
            throw new Error('monogram settings incomplete');
          }
          return place;
        });
      // A failed lookup is not kept, so the next try (e.g. at checkout) asks again
      this.placementCache[productId].catch(() => { delete this.placementCache[productId]; });
    }
    return this.placementCache[productId];
  }

  // Loads the same two thread fonts the product-page popup uses (once per visit)
  loadMonogramFont(fontName) {
    const files = { Italic: 'Calgary-mediumitalic.ttf', Typewriter: 'Censcbk.ttf' };
    const file = files[fontName] || files.Italic;
    const family = `gb-mono-${fontName === 'Typewriter' ? 'typewriter' : 'italic'}`;
    if (!this.fontCache[family]) {
      const face = new FontFace(family, `url(https://cdn-zeptoapps.com/product-personalizer/font/eberjey-store.myshopify.com/${file})`);
      this.fontCache[family] = face.load().then(loaded => { document.fonts.add(loaded); return family; });
      this.fontCache[family].catch(() => { delete this.fontCache[family]; });
    }
    return this.fontCache[family];
  }

  // Loads a product photo so the canvas may read it back (Shopify's CDN allows this)
  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';               // without this the finished canvas can't be saved
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('photo failed to load'));
      img.src = src;
    });
  }

  async drawPreview(item) {
    const [place, family] = await Promise.all([this.getPlacement(item.productId), this.loadMonogramFont(item.monogram.font)]);
    const src = new URL(place.image);
    src.searchParams.set('width', '800');           // smaller download than the 1333px original
    const img = await this.loadImage(src.toString());

    // 400px wide, same shape as the photo (product-page previews are 333-399px wide)
    const W = 400;
    const H = Math.round(W * img.naturalHeight / img.naturalWidth);
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, W, H);

    // Placement maths copied from the monogram app, so letters land where the product page puts them.
    // Checked against the app's own drawings on 3 products and 2 fonts (within about 1-2px).
    const g = place.x / 100 * W;                    // box left, px
    const top = place.y / 100 * H;                  // box top, px
    const boxW = place.w / 100 * W;                 // box width, px
    const boxH = place.h / 100 * H;                 // box height, px
    const r = place.rot * Math.PI / 180;
    const half = boxW / 2;
    ctx.save();
    ctx.translate(g - (half - half * Math.cos(r)) - boxH * Math.sin(r),   // app's pivot correction, across
                  top - half * Math.sin(r) + (boxH - boxH * Math.cos(r))); // app's pivot correction, down
    ctx.rotate(-r);                                 // e.g. 338° in the settings = 22° clockwise on the pocket

    // Letter size: 0.77 of the box height (measured), shrunk if the word is wider than the box
    const text = item.monogram.text;
    let size = boxH * 0.77;
    ctx.font = `${size}px "${family}"`;
    const width = ctx.measureText(text).width;
    if (width > boxW) {
      size = size * boxW / width;
      ctx.font = `${size}px "${family}"`;
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = place.color;
    ctx.fillText(text, 0, boxH * 0.37);             // 0.37 of box height below the pivot (measured)
    ctx.restore();

    // JPEG keeps the upload small (about 40KB vs about 300KB for the product page's PNG).
    // toDataURL is instant; toBlob can stall about 1s if the shopper has switched browser tabs.
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    const bytes = atob(dataUrl.split(',')[1]);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
    return new Blob([buffer], { type: 'image/jpeg' });
  }

  /* ---- GIFT RULES (rev 15): each step's minimum / maximum + a minimum for the whole gift ---- */
  stepCount(stepNum) {
    return this.heldItems.filter(i => i.step === String(stepNum)).length;
  }

  // True when the step has a maximum and already holds that many items
  isStepFull(stepNum) {
    const st = this.stepState[stepNum];
    return !!st && st.maxForStep > 0 && this.stepCount(stepNum) >= st.maxForStep;
  }

  // A step counts as done once it has its minimum; an optional step once it has anything
  isStepDone(stepNum) {
    const st = this.stepState[stepNum];
    const count = this.stepCount(stepNum);
    return !!st && count >= st.minForStep && (st.minForStep > 0 || count > 0);
  }

  // What is still missing, step by step, e.g. "Add 1 more Gisele PJ to complete your gift"; '' when complete
  missingMessage() {
    for (const [num, st] of Object.entries(this.stepRules ? this.stepState : {})) {
      const need = st.minForStep - this.stepCount(num);
      if (need > 0) return `Add ${need} more ${need === 1 ? st.one : st.many} to complete your gift`;
    }
    const need = this.minItems - this.heldItems.length;
    if (need > 0) return `Add ${need} more item${need === 1 ? '' : 's'} to complete your gift`;
    return '';
  }

  isReady() {
    return this.missingMessage() === '';
  }

  // Share of the rules met (0 to 1): each step's minimum first, then any extra items the whole gift needs
  progressRatio() {
    let met = 0;
    let required = 0;
    Object.entries(this.stepRules ? this.stepState : {}).forEach(([num, st]) => {
      required += st.minForStep;
      met += Math.min(this.stepCount(num), st.minForStep);
    });
    const needed = Math.max(this.minItems, required);
    if (!needed) return 1;
    const extra = Math.min(this.heldItems.length - met, needed - required);   // items beyond step minimums
    return Math.min(1, (met + extra) / needed);
  }

  // Ticks the rules strip and step badges, and redraws the gift box checklist(s)
  renderRules() {
    const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const steps = Object.entries(this.stepState);
    let stepMins = 0;
    const lines = steps.map(([num, st], idx) => {
      const count = this.stepCount(num);
      const done = this.isStepDone(num);
      stepMins += st.minForStep;
      this.page.querySelector(`[data-gb-rule-step="${num}"]`)?.classList.toggle('is-done', done);
      document.getElementById(`gb-step-${num}`)?.querySelector('[data-gb-step-badge]')?.classList.toggle('is-done', done);

      const rawName = st.minForStep === 1 ? st.one : st.many;            // "Gisele PJ" / "self-care essentials"
      const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);    // rev 19: capital first letter (now follows "Step 1")
      const countText = st.minForStep === 0
        ? (count ? `${count} added` : 'Optional')                         // optional step
        : (done ? `${count} added` : `${count} of ${st.minForStep}`);    // required step
      // Rev 19: numbered box (the step's number, a tick once done), "Step 1 · Gisele PJ", like the strip above Step 1
      // Rev 23: no number box; the tick shows after the count once done (CSS)
      return `<li class="gb-checklist__item${done ? ' is-done' : ''}">`
        + `<span class="gb-checklist__name"><span class="gb-checklist__step">Step ${idx + 1}</span> ${esc(name)}</span><span class="gb-checklist__count">${countText}</span></li>`;
    });

    // Whole-gift minimum, only when it asks for more than the steps do
    const total = this.heldItems.length;
    const totalDone = total >= this.minItems;
    this.page.querySelector('[data-gb-rule-total]')?.classList.toggle('is-done', totalDone);
    if (this.minItems > stepMins) {
      lines.push(`<li class="gb-checklist__item${totalDone ? ' is-done' : ''}">`
        + `<span class="gb-checklist__name">Items in total</span><span class="gb-checklist__count">${totalDone ? `${total} added` : `${total} of ${this.minItems}`}</span></li>`);   // rev 16: "4 added" once met
    }
    // The phone drawer sits outside #gift-builder-v2, so look in the whole page
    document.querySelectorAll('[data-gb-checklist]').forEach(list => { list.innerHTML = lines.join(''); });

    // Rev 27: "Reset" shows in a step's header only once a product from that step is in the gift box
    this.page.querySelectorAll('[data-gb-step-reset]').forEach(btn => {
      btn.hidden = !(this.stepCount(btn.dataset.gbStepReset) > 0);
    });
    if (this.v4) this.renderV4Nav();                              // V4: step list status lines and ticks
  }

  /* ---- STEP ACCORDION (unchanged behaviour) ---- */
  bindStepAccordionEvents() {
    this.page.querySelectorAll('[data-gb-step-toggle]').forEach(header => {
      header.addEventListener('click', () => {
        const stepNum = header.dataset.gbStepToggle;
        this.toggleStep(stepNum, !this.stepState[stepNum].open);
      });
    });
  }

  toggleStep(stepNum, open) {
    if (this.v4) return;                         // V4: steps never open or close
    const state = this.stepState[stepNum];
    if (!state) return;
    state.open = open;
    const section = document.getElementById(`gb-step-${stepNum}`);
    const header = this.page.querySelector(`[data-gb-step-toggle="${stepNum}"]`);
    if (section) section.classList.toggle('gb-step--collapsed', !open);
    if (header) header.setAttribute('aria-expanded', String(open));
  }

  evaluateStepAccordion() {
    Object.keys(this.stepState).forEach(stepNum => {
      const state = this.stepState[stepNum];
      const count = this.heldItems.filter(i => i.step === stepNum).length;

      const summary = document.getElementById(`gb-step-${stepNum}-summary`);
      if (summary) {
        summary.hidden = count === 0;
        summary.textContent = count > 0 ? `✓ ${count} item${count === 1 ? '' : 's'} selected` : '';
      }

      // Rev 19: closing a finished step happens in autoAdvance(), right after the add that completes it.
    });
  }

  /* ---- STYLE TILE ROW SCROLLING (unchanged) ---- */
  bindColsScrollEvents() {
    const cols = this.page.querySelector('.gb-cols');
    const prevBtn = document.getElementById('gb-cols-prev');
    const nextBtn = document.getElementById('gb-cols-next');
    if (!cols || !prevBtn || !nextBtn) return;

    const scrollByAmount = () => cols.clientWidth * 0.8;
    prevBtn.addEventListener('click', () => cols.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' }));
    nextBtn.addEventListener('click', () => cols.scrollBy({ left: scrollByAmount(), behavior: 'smooth' }));

    const updateArrows = () => {
      const maxScroll = cols.scrollWidth - cols.clientWidth;
      prevBtn.hidden = cols.scrollLeft <= 4;
      nextBtn.hidden = maxScroll <= 4 || cols.scrollLeft >= maxScroll - 4;
    };
    cols.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);
    updateArrows();
  }

  /* ---- STYLE FILTER TABS (unchanged): first tab is active on load ---- */
  bindTabEvents() {
    this.page.querySelectorAll('.gb-col').forEach(tab => {
      tab.addEventListener('click', () => this.setFilter(tab.dataset.filter, tab));
    });
    document.getElementById('gb-no-results')?.querySelector('.gb-no-results__reset')
      ?.addEventListener('click', () => {
        const firstTab = this.page.querySelector('.gb-col');
        if (firstTab) this.setFilter(firstTab.dataset.filter, firstTab);
      });

    const activeTab = this.page.querySelector('.gb-col--active');
    if (activeTab) this.setFilter(activeTab.dataset.filter, activeTab);
  }

  setFilter(filterKey, activeTab) {
    this.page.querySelectorAll('.gb-col').forEach(t => {
      t.classList.remove('gb-col--active');
      t.setAttribute('aria-selected', 'false');
    });
    activeTab.classList.add('gb-col--active');
    activeTab.setAttribute('aria-selected', 'true');

    const cards = document.querySelectorAll('#gb-gisele-grid .gb-card');
    let visible = 0;
    cards.forEach(card => {
      const styleKeys = (card.dataset.style || '').split(' ').filter(Boolean);
      const show = filterKey === 'all' || styleKeys.includes(filterKey);
      card.classList.toggle('gb-card--hidden', !show);
      if (show) visible++;
    });

    const noResults = document.getElementById('gb-no-results');
    const grid = document.getElementById('gb-gisele-grid');
    if (noResults && grid) {
      noResults.hidden = visible > 0;
      grid.hidden = visible === 0;
    }

    const label = document.getElementById('gb-filter-label');
    if (label) {
      const titleEl = activeTab.querySelector('.gb-col__title');
      const titleText = titleEl ? titleEl.textContent.trim() : '';
      label.textContent = filterKey === 'all' ? 'Showing all styles' : `Showing: ${titleText}`;
    }

    // Rev 13: a new style starts at the top of Step 1's scroll window, and its counter recounts
    this.refreshCarousel(grid);                                   // rev 30: phones - back to the first card, arrows updated
    const win = (this.gridWindows || []).find(w => w.grid === grid);
    if (win) {
      win.grid.scrollTop = 0;
      this.updateGridCounter(win);
    }
  }

  /* ---- PHOTOS CYCLE ON HOVER (rev 23) ----
     Same as collection page cards: on a desktop with a mouse, hovering a card moves through its photos
     (the theme's own delay, 1.5s each) with a bar filling for each; leaving the card goes back to photo 1. */
  bindCardSlides() {
    if (!(window.matchMedia && window.matchMedia('(hover: hover) and (min-width: 769px)').matches)) return;   // phones/tablets: first photo only
    const setting = window.theme && window.theme.settings && Number(window.theme.settings.cycle_images_hover_delay);
    const delay = (setting > 0 ? setting : 1.5) * 1000;
    this.page.querySelectorAll('.gb-card').forEach(card => {
      const media = card.querySelector('.gb-card__media[data-slides]');
      if (!media) return;
      const photos = [...media.querySelectorAll('.gb-card__image')];
      const bars = [...media.querySelectorAll('.gb-card__pages span')];
      if (photos.length < 2) return;
      let index = 0;
      let started = 0;
      let frame = null;
      const show = i => {
        index = i % photos.length;
        photos.forEach((p, k) => p.classList.toggle('is-current', k === index));
        bars.forEach((b, k) => {
          b.classList.toggle('is-active', k === index);
          b.style.setProperty('--gb-page-w', k < index ? '100%' : '0%');   // earlier photos full, later ones empty
        });
      };
      const tick = now => {
        const pct = Math.min(100, ((now - started) / delay) * 100);
        if (bars[index]) bars[index].style.setProperty('--gb-page-w', `${pct}%`);
        if (pct >= 100) { show(index + 1); started = now; }
        frame = requestAnimationFrame(tick);
      };
      card.addEventListener('mouseenter', () => {
        photos.forEach(p => { if (p.getAttribute('loading') === 'lazy') p.setAttribute('loading', 'eager'); });   // fetch the other photos now
        started = performance.now();
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(tick);
      });
      card.addEventListener('mouseleave', () => { cancelAnimationFrame(frame); show(0); });
    });
  }

  /* ---- PICK A COLLECTION FIRST (rev 23) ----
     A step can show one tile per collection. Nothing is picked at first, so no products show; picking a
     tile shows that collection's products (cards are tagged "c1", "c2"...). Only one tile is picked at a time.
     Nothing is picked at the start, after a refresh, or when the box already holds items from that step (rev 29). */
  bindChoices() {
    this.page.querySelectorAll('[data-gb-choices]').forEach(group => {
      const stepNum = group.dataset.gbChoices;
      const tiles = [...group.querySelectorAll('.gb-choice')];
      tiles.forEach(tile => tile.addEventListener('click', () => this.pickChoice(stepNum, tile.dataset.gbChoice)));
      // Rev 29: nothing is ever picked for the shopper - not even after a refresh or with items already in the box
      try { sessionStorage.removeItem(`gb-v2-choice-${stepNum}`); } catch (e) { /* storage blocked */ }

      // Rev 24: arrows scroll the row by most of its width; each hides at its end of the row
      const wrap = group.closest('.gb-choices-wrap');
      const prev = wrap && wrap.querySelector('[data-gb-choices-prev]');
      const next = wrap && wrap.querySelector('[data-gb-choices-next]');
      if (!prev || !next) return;
      const update = () => {
        const max = group.scrollWidth - group.clientWidth;
        prev.hidden = group.scrollLeft <= 4;                     // at the start: no back arrow
        next.hidden = group.scrollLeft >= max - 4;               // at the end (or nothing to scroll): no next arrow
      };
      const step = () => Math.max(group.clientWidth * 0.8, 160);
      prev.addEventListener('click', () => group.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => group.scrollBy({ left: step(), behavior: 'smooth' }));
      group.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      window.addEventListener('load', update);                    // tile images change the row width once loaded
      update();
    });
  }

  pickChoice(stepNum, key, quiet) {
    const step = document.getElementById(`gb-step-${stepNum}`);
    if (!step) return;
    step.querySelectorAll('.gb-choice').forEach(t => t.setAttribute('aria-checked', String(!!key && t.dataset.gbChoice === key)));
    const grid = step.querySelector('[data-gb-choice-grid]');
    const prompt = step.querySelector('[data-gb-choice-prompt]');
    if (grid) {
      grid.querySelectorAll('.gb-card').forEach(card => {
        const keys = (card.dataset.style || '').split(' ').filter(Boolean);
        card.classList.toggle('gb-card--hidden', !key || !keys.includes(key));
      });
      grid.hidden = !key;                                         // nothing picked: no products at all
      grid.scrollTop = 0;
    }
    if (prompt) prompt.hidden = !!key;
    // Rev 30: the other tiles fade back, and a heading names the picked group above its products
    step.querySelector('[data-gb-choices]')?.classList.toggle('has-pick', !!key);
    const head = step.querySelector('[data-gb-choice-head]');
    if (head) {
      const tile = key && step.querySelector(`.gb-choice[data-gb-choice="${key}"]`);
      const shown = grid ? grid.querySelectorAll('.gb-card:not(.gb-card--hidden)').length : 0;
      head.hidden = !tile;
      if (tile) {
        head.querySelector('[data-gb-choice-head-title]').textContent = (tile.querySelector('.gb-choice__title')?.textContent || '').trim();
        head.querySelector('[data-gb-choice-head-count]').textContent = `${shown} ${shown === 1 ? 'style' : 'styles'}`;
      }
    }
    if (this.gridWindows) this.sizeGridWindow();                  // the box now has products to measure
    this.refreshCarousel(grid);                                   // rev 30: phones - arrows for the new set
    if (!quiet) this.renderRules();                               // Reset button follows the pick
    if (this.v4) this.updateV4Pick(step, key);                    // V4: tiles hide, the switch shows
  }

  /* ---- RESET A STEP (rev 23) ----
     "Reset" sits inside the step header (which itself opens/closes the step), so its click must not reach
     the header. It removes every item of that step from the gift box and un-picks a picked collection. */
  bindStepResets() {
    this.page.querySelectorAll('[data-gb-step-reset]').forEach(btn => {
      const run = e => {
        e.preventDefault();
        e.stopPropagation();                                      // don't open/close the step as well
        this.resetStep(btn.dataset.gbStepReset);
      };
      btn.addEventListener('click', run);
      btn.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') run(e); });
    });
  }

  resetStep(stepNum) {
    clearTimeout(this.advanceTimer);                              // a pending auto-close no longer applies
    this.heldItems.filter(i => i.step === stepNum).forEach(i => delete this.previewJobs[i.uid]);
    this.heldItems = this.heldItems.filter(i => i.step !== stepNum);
    this.saveHeldItems();
    if (this.page.querySelector(`[data-gb-choices="${stepNum}"]`)) this.pickChoice(stepNum, null, true);
    this.toggleStep(stepNum, true);                               // open it so the shopper can pick again
    this.updateUI();
  }

  /* ---- V4: STEP LIST (desktop) / STEP TABS (phones) ----
     Every step is on the page but only the chosen one shows. The step list is the only way to move between steps. */
  bindV4Steps() {
    this.v4Buttons = [...this.page.querySelectorAll('[data-gb-v4-step]')];
    this.v4Buttons.forEach(btn => btn.addEventListener('click', () => this.showStep(btn.dataset.gbV4Step, true)));
    // V4: the switch in a "pick a collection" step header changes collection
    this.page.querySelectorAll('[data-gb-v4-switch-to]').forEach(btn => btn.addEventListener('click', () => {
      const step = btn.closest('.gb-step');
      if (step && btn.getAttribute('aria-pressed') !== 'true') this.pickChoice(step.id.replace('gb-step-', ''), btn.dataset.gbV4SwitchTo);
    }));
    // V4: start on step 1; after a refresh with picks held, start on the first step still missing something
    const order = Object.keys(this.stepState);
    const start = (this.heldItems.length && order.find(n => this.stepState[n].minForStep > 0 && !this.isStepDone(n))) || order[0];
    this.showStep(start, false);
    // V4: in the theme editor, selecting a step block shows that step
    document.addEventListener('shopify:block:select', e => {
      const step = e.target && e.target.closest && e.target.closest('.gb-step');
      if (step && this.page.contains(step)) this.showStep(step.id.replace('gb-step-', ''), false);
    });
    this.bindV4Layout();
  }

  showStep(stepNum, byShopper) {
    stepNum = String(stepNum);
    if (byShopper === true) clearTimeout(this.advanceTimer);    // V4 rev 2: the shopper's own click wins over a pending auto move
    const section = document.getElementById(`gb-step-${stepNum}`);
    if (!this.stepState[stepNum] || !section) return;
    this.activeStep = stepNum;
    this.page.querySelectorAll('.gb-v4-step').forEach(s => s.classList.toggle('gb-v4-step--off', s !== section));
    this.renderV4Nav();
    // its carousel was hidden until now: measure the arrows again (phones)
    const grid = section.querySelector('.gb-grid');
    (this.carousels || []).filter(c => c.grid === grid).forEach(c => c.update());
    if (!byShopper) return;
    // V4: if the step's top is above the screen (shopper had scrolled down), bring it into view below the header
    // V4 rev 2: an auto move also scrolls when the new step's header sits low on the screen
    const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
    const top = section.getBoundingClientRect().top;
    const low = byShopper === 'auto' && top > Math.max(margin, window.innerHeight * 0.5);
    if (top < margin - 1 || low) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* V4 rev 2: the add that brings the step to its minimum moves the page on to the next unfinished step (after a short
     pause so "Added to Gift ✓" is seen). Only after a shopper's add (never on load or restore), only when that add
     completes the step, never from an optional step. Everything complete: stay put ("Ready to gift ✓" shows). */
  v4AutoAdvance(stepNum) {
    stepNum = String(stepNum);
    const state = this.stepState[stepNum];
    if (!state || state.minForStep < 1 || this.stepCount(stepNum) !== state.minForStep) return;
    const order = Object.keys(this.stepState);
    const next = order.slice(order.indexOf(stepNum) + 1).find(n => !this.isStepDone(n))
      || order.find(n => n !== stepNum && !this.isStepDone(n));   // an earlier step still short
    clearTimeout(this.advanceTimer);
    if (!next) return;
    this.advanceTimer = setTimeout(() => {
      if (this.stepCount(stepNum) < state.minForStep) return;    // an item was removed meanwhile
      if (this.activeStep !== stepNum || this.isStepDone(next)) return;   // the shopper already moved, or the target filled up
      this.showStep(next, 'auto');
    }, 700);
  }

  // V4: "0 of 1 · Viewing now", "1 of 1 · Tap to edit", "0 of 1"; the number turns into a black tick once done
  renderV4Nav() {
    (this.v4Buttons || []).forEach(btn => {
      const n = btn.dataset.gbV4Step;
      const st = this.stepState[n];
      if (!st) return;
      const count = this.stepCount(n);
      const done = this.isStepDone(n);
      const active = n === this.activeStep;
      btn.classList.toggle('is-active', active);
      btn.classList.toggle('is-done', done);
      if (active) btn.setAttribute('aria-current', 'step'); else btn.removeAttribute('aria-current');
      const countText = st.minForStep === 0 ? (count ? `${count} added` : 'Optional') : `${count} of ${st.minForStep}`;
      const hint = active ? 'Viewing now' : (count ? 'Tap to edit' : '');
      const status = btn.querySelector('[data-gb-v4-status]');
      if (status) status.innerHTML = `<span class="gb-v4-nav__count">${countText}</span>` + (hint ? `<span class="gb-v4-nav__hint"> · ${hint}</span>` : '');
    });
  }

  // V4: after a pick the tiles step aside and the header switch shows the picked collection; no pick = tiles again
  updateV4Pick(step, key) {
    const picker = step.querySelector('[data-gb-v4-picker]');
    if (picker) picker.hidden = !!key;
    const sw = step.querySelector('[data-gb-v4-switch]');
    if (sw) {
      sw.hidden = !key;
      sw.querySelectorAll('[data-gb-v4-switch-to]').forEach(b => {
        const on = b.dataset.gbV4SwitchTo === key;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', String(on));
      });
    }
    const title = step.querySelector('[data-gb-v4-title]');
    const tile = key && step.querySelector(`.gb-choice[data-gb-choice="${key}"]`);
    if (title) title.textContent = (tile && tile.dataset.gbV4Title) || title.dataset.base || title.textContent;
  }

  /* V4 rev 2: the left column is always pinned on desktop (CSS: sticky, capped to the screen height, scrolls inside
     itself only on very short screens). Phones: the tab row's height is published so a step scrolled into view
     lands just below the tabs. */
  bindV4Layout() {
    const side = this.page.querySelector('[data-gb-v4-side]');
    const nav = this.page.querySelector('[data-gb-v4-nav]');
    if (!side) return;
    const check = () => {
      const desktop = this.isDesktopWindow();
      this.page.style.setProperty('--gb-v4-tabs-h', `${!desktop && nav ? Math.round(nav.offsetHeight) : 0}px`);
    };
    check();
    if (window.ResizeObserver) new ResizeObserver(check).observe(side);
    window.addEventListener('resize', check);
  }

  /* ---- PINNED RULES STRIP (rev 19) ----
     The strip of step boxes ("1 Choose at least 1 Gisele PJ ...") stays pinned under the site header.
     Its height is published as --gb-rules-h so the step headers and the gift box pin just below it,
     and it gets a hairline underneath once it is actually pinned. */
  bindStickyRules() {
    const strip = this.page.querySelector('.gb-rules');
    if (!strip) return;                                          // strip switched off in the editor
    const publish = () => {
      // Rev 30: phones stack the strip and don't pin it, so it adds nothing to the pinned headers there
      const pinned = getComputedStyle(strip).position === 'sticky';
      this.page.style.setProperty('--gb-rules-h', `${pinned ? Math.round(strip.offsetHeight) : 0}px`);
      if (this.gridWindows) this.sizeGridWindow();               // step windows lose that much height
    };
    publish();
    if (window.ResizeObserver) new ResizeObserver(publish).observe(strip);
    else window.addEventListener('resize', publish);
    let ticking = false;
    const markStuck = () => {
      ticking = false;
      const pinAt = parseFloat(getComputedStyle(strip).top) || 0;
      strip.classList.toggle('is-stuck', strip.getBoundingClientRect().top <= pinAt + 0.5 && window.scrollY > 0);
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(markStuck); } }, { passive: true });
    markStuck();
  }

  /* ---- STEP SCROLL WINDOWS (rev 13; rev 14 = every step, desktop only: wider than 900px) ---- */
  // Each step's products scroll inside a screen-sized box with a visible scrollbar and a "Viewing 9–16 of 40" counter.
  // The wheel moves the page until a box reaches its step header, then the products; phones scroll normally.
  bindGridWindow() {
    this.gridWindows = [];
    this.page.querySelectorAll('.gb-step').forEach(step => {
      const grid = step.querySelector('.gb-grid');
      if (!grid) return;                                          // a step without products: nothing to do
      const win = { grid, header: step.querySelector('.gb-step__header') };
      grid.classList.add('gb-grid--window');                     // the CSS for the box hangs off this class

      // Counter line under the grid: rev 20 sections print it with the page (no jump); older ones get it here
      const printed = (grid.closest('.gb-grid-wrap') || grid).nextElementSibling;   // rev 30: grids sit in a carousel wrap
      if (printed && printed.classList.contains('gb-grid-counter')) {
        win.counter = printed;
      } else {
        win.counter = document.createElement('p');
        win.counter.className = 'gb-grid-counter';
        win.counter.setAttribute('aria-live', 'polite');         // screen readers hear the new range
        grid.after(win.counter);
      }

      grid.addEventListener('scroll', () => this.updateGridCounter(win), { passive: true });

      // Wheel over a box not yet at the top of the screen: move the page, not the box
      grid.addEventListener('wheel', e => {
        if (!this.isDesktopWindow() || e.deltaY <= 0) return;    // only when scrolling down on desktop
        // Where the step header sits once pinned (site header height + its own height), not where it is now,
        // so the page scrolls at full speed until the products reach that line
        const dockLine = win.header ? (parseFloat(getComputedStyle(win.header).top) || 0) + win.header.offsetHeight : 0;
        const gap = grid.getBoundingClientRect().top - dockLine; // distance still to travel
        if (gap > 4) {
          e.preventDefault();
          // Instant, not the theme's smooth scroll: fast wheel spins can't overshoot the header
          window.scrollBy({ top: Math.min(e.deltaY, gap), behavior: 'instant' });   // stop exactly at the step header
        }
      }, { passive: false });

      this.gridWindows.push(win);
    });

    window.addEventListener('resize', () => this.sizeGridWindow());
    // Product photos load late and change row heights: re-measure once the page has fully loaded
    window.addEventListener('load', () => this.sizeGridWindow());
    this.sizeGridWindow();
  }

  isDesktopWindow() {
    return !!(window.matchMedia && window.matchMedia('(min-width: 901px)').matches);  // very old browsers: no window
  }

  // Box height = screen height minus the site header, that step's pinned header and the counter line
  sizeGridWindow() {
    (this.gridWindows || []).forEach(win => {
      if (this.isDesktopWindow()) {
        const headerTop = win.header ? parseFloat(getComputedStyle(win.header).top) || 0 : 0;   // site header height
        const headerH = win.header ? win.header.offsetHeight : 0;                              // step header height
        const counterH = 34;                                                                     // counter line + gap
        const h = Math.max(420, window.innerHeight - headerTop - headerH - counterH - 16);
        win.grid.style.setProperty('--gb-window-h', `${Math.round(h)}px`);
      }
      this.updateGridCounter(win);
    });
  }

  // "Viewing 9–16 of 40": counts the cards at least half visible inside one step's box
  updateGridCounter(win) {
    if (!win) return;
    const grid = win.grid;
    const scrollable = this.isDesktopWindow() && grid.scrollHeight > grid.clientHeight + 4;
    win.counter.hidden = !scrollable;                            // nothing to scroll: no counter
    if (!scrollable) return;

    const box = grid.getBoundingClientRect();
    const cards = [...grid.querySelectorAll('.gb-card:not(.gb-card--hidden)')];
    let first = 0;
    let last = 0;
    cards.forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const shown = Math.min(r.bottom, box.bottom) - Math.max(r.top, box.top);
      if (shown >= r.height / 2) {
        if (!first) first = i + 1;
        last = i + 1;
      }
    });
    if (!first) return;                                          // mid-jump: keep the last text
    const atEnd = grid.scrollTop + grid.clientHeight >= grid.scrollHeight - 4;
    win.counter.textContent = `Viewing ${first}–${last} of ${cards.length}` + (atEnd ? '' : ' · scroll for more');
  }

  /* ---- UI ---- */
  updateUI() {
    const items = this.heldItems;
    const count = items.length;
    // Rev 15: a free gift box / monogram adds nothing; a paid one adds its price (gift box only once items exist)
    const monoCount = this.monogramFree ? 0 : items.filter(i => i.monogram).length;
    const boxCost = !this.giftBoxFree && this.giftBoxChecked && count > 0 ? this.giftBoxPrice : 0;
    const total = items.reduce((s, i) => s + i.price, 0) + monoCount * this.monogramPrice + boxCost;
    const ready = this.isReady();                                // every step's minimum + the whole-gift minimum
    // Rev 21: "Ready to gift ✓" tag next to "My Gift Box" (sidebar and phone drawer)
    document.querySelectorAll('[data-gb-ready]').forEach(tag => { tag.hidden = !ready; });

    // Card check badges + button labels: a card is "selected" when at least one of its items is in the box
    this.page.querySelectorAll('.gb-card').forEach(card => {
      card.classList.toggle('gb-card--selected', items.some(i => i.productId === card.dataset.productId));
      this.refreshCardButtons(card);
    });

    this.renderItemLists(items);
    this.updateProgress(count, ready);
    this.updateTotals(this.formatMoney(total));
    this.updateCTAs(ready, count);
    this.updateMobileBar(count, this.formatMoney(total));
    this.toggleEmptyStates(count);
    this.evaluateStepAccordion();
    this.renderRules();                                          // rev 15: strip, badges, checklist
  }

  renderItemLists(items) {
    const html = items.map(item => this.itemHTML(item)).join('');
    ['gb-selected-items', 'gb-drawer-items'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = html;
    });
    this.page.querySelectorAll('.gb-selected-item__remove').forEach(btn => {
      btn.addEventListener('click', () => this.removeHeldItem(btn.dataset.uid));
    });
    // The mobile drawer sits outside #gift-builder-v2, so wire its remove buttons too
    document.querySelectorAll('#gb-drawer .gb-selected-item__remove').forEach(btn => {
      btn.addEventListener('click', () => this.removeHeldItem(btn.dataset.uid));
    });
  }

  // One line in the sidebar. Monogram fee shown only when monogramming is not free (rev 15).
  itemHTML(item) {
    const fee = !this.monogramFree && item.monogram ? ` · +${this.formatMoney(this.monogramPrice)}` : '';
    const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    return `<li class="gb-selected-item">
      <img src="${esc(item.image)}" alt="${esc(item.title)}" class="gb-selected-item__image" width="48" height="64" loading="lazy">
      <div class="gb-selected-item__details">
        <p class="gb-selected-item__name">${esc(item.title)}</p>
        ${item.color ? `<p class="gb-selected-item__size">${esc(item.color)}</p>` : ''}
        ${item.size ? `<p class="gb-selected-item__size">Size: ${esc(item.size)}</p>` : ''}
        ${item.monogram ? `<p class="gb-selected-item__size">Monogram: ${esc(item.monogram.text)} (${esc(item.monogram.font)})${fee}</p>` : ''}
        <p class="gb-selected-item__price">${this.formatMoney(item.price)}</p>
      </div>
      <button class="gb-selected-item__remove" data-uid="${esc(item.uid)}" aria-label="Remove ${esc(item.title)}" type="button">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
      </button>
    </li>`;
  }

  updateProgress(count, ready) {
    const pct = Math.round(this.progressRatio() * 100);          // rev 15: counts each step's minimum, not just items
    ['gb-progress-fill', 'gb-drawer-progress-fill'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.width = pct + '%';
    });
    ['gb-progress-text', 'gb-drawer-progress-text'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (ready) {
        el.textContent = "You're all set — ready to gift!";
        el.classList.add('gb-progress-text--ready');
      } else {
        el.textContent = this.missingMessage();                  // e.g. "Add 1 more Gisele PJ to complete your gift"
        el.classList.remove('gb-progress-text--ready');
      }
    });
  }

  updateTotals(formatted) {
    ['gb-total-price', 'gb-drawer-total'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.textContent = formatted;
    });
  }

  updateCTAs(ready, count) {
    ['gb-add-to-cart', 'gb-drawer-add-to-cart'].forEach(id => {
      const btn = document.getElementById(id);
      if (!btn) return;
      btn.disabled = !ready;
      btn.setAttribute('aria-disabled', String(!ready));
    });
    ['gb-cta-note', 'gb-drawer-note'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.classList.remove('gb-cta-note--error');
      // Rev 17: the only status line: what is missing, or ready (mentions packaging only with the gift box ticked)
      el.textContent = ready
        ? (this.giftBoxChecked && this.giftBoxVariantId ? 'Ready to gift — ships beautifully packaged' : 'Ready to gift')
        : this.missingMessage();
    });
  }

  updateMobileBar(count, formatted) {
    const c = document.getElementById('gb-mobile-count');
    const t = document.getElementById('gb-mobile-total');
    if (c) c.textContent = `${count} item${count !== 1 ? 's' : ''} selected`;
    if (t) t.textContent = formatted;
    // Rev 30: one line per step - what is picked there ("Gisele Long PJ Set · M +1 more"), or "Not selected yet"
    document.querySelectorAll('[data-gb-mobile-step]').forEach(line => {
      const items = this.heldItems.filter(i => i.step === line.dataset.gbMobileStep);
      const pick = line.querySelector('[data-gb-mobile-pick]');
      line.classList.toggle('is-empty', !items.length);
      line.classList.toggle('is-done', this.isStepDone(line.dataset.gbMobileStep));
      if (!pick) return;
      if (!items.length) { pick.textContent = 'Not selected yet'; return; }
      const first = items[0];
      pick.textContent = first.title + (first.size ? ` · ${first.size}` : '') + (items.length > 1 ? ` +${items.length - 1} more` : '');
    });
    this.measureMobileBar();
  }

  // Rev 30: the bar's height changes with its lines; the page keeps that much room at the bottom so nothing hides under it
  measureMobileBar() {
    const bar = document.getElementById('gb-mobile-bar');
    if (!bar || !bar.offsetHeight) return;                        // desktop: bar not shown
    const h = `${Math.round(bar.offsetHeight)}px`;
    [this.page, bar, document.documentElement].forEach(el => el && el.style.setProperty('--gb-mobile-bar-h', h));   // rev 32: also on the page root, so the accessibility icon can sit above the bar
  }

  /* ---- PHONE CAROUSELS (rev 30) ----
     On phones each step's products sit in one sideways row (2 cards and a peek of the next), with round arrows
     on the sides like the site's recommendation carousels. The arrows sit halfway down the photos and hide at each end. */
  bindGridCarousels() {
    this.carousels = [];
    this.page.querySelectorAll('.gb-grid-wrap').forEach(wrap => {
      const grid = wrap.querySelector('.gb-grid');
      const prev = wrap.querySelector('[data-gb-grid-prev]');
      const next = wrap.querySelector('[data-gb-grid-next]');
      if (!grid || !prev || !next) return;
      const update = () => {
        const phone = !(window.matchMedia && window.matchMedia('(min-width: 901px)').matches);
        const max = grid.scrollWidth - grid.clientWidth;
        prev.hidden = !phone || grid.scrollLeft <= 4;
        next.hidden = !phone || grid.scrollLeft >= max - 4;
        const photo = grid.querySelector('.gb-card:not(.gb-card--hidden) .gb-card__media');
        if (photo) wrap.style.setProperty('--gb-arrow-top', `${Math.round(photo.offsetHeight / 2)}px`);
      };
      const step = dir => {
        const card = grid.querySelector('.gb-card:not(.gb-card--hidden)');
        const by = card ? card.offsetWidth + (parseFloat(getComputedStyle(grid).columnGap) || 0) : grid.clientWidth * 0.8;   // one card at a time (card + the gap between cards)
        grid.scrollBy({ left: dir * by, behavior: 'smooth' });
      };
      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      grid.addEventListener('scroll', update, { passive: true });
      this.carousels.push({ grid, update });
      update();
    });
    const all = () => (this.carousels || []).forEach(c => c.update());
    window.addEventListener('resize', () => { all(); this.measureMobileBar(); });
    window.addEventListener('load', () => { all(); this.measureMobileBar(); });   // photos change the arrow height
    document.querySelector('[data-gb-mobile-steps]')?.addEventListener('click', () => this.openDrawer());
    document.querySelector('[data-gb-mobile-steps]')?.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openDrawer(); } });
  }

  refreshCarousel(grid) {
    if (!grid) return;
    grid.scrollLeft = 0;                                          // a new set of products starts at the first one
    (this.carousels || []).filter(c => c.grid === grid).forEach(c => c.update());
  }

  toggleEmptyStates(count) {
    [['gb-empty-state', 'gb-selected-items'], ['gb-drawer-empty', 'gb-drawer-items']].forEach(([emptyId, listId]) => {
      const empty = document.getElementById(emptyId);
      const list = document.getElementById(listId);
      if (!empty || !list) return;
      empty.hidden = count > 0;
      list.hidden = count === 0;
    });
  }

  /* ---- GIFT BOX CHECKBOX (desktop + mobile kept in sync; remembered across a refresh) ---- */
  bindGiftBoxEvents() {
    // Bring back the last choice after a refresh (default: the theme editor's starting tick)
    try {
      const saved = sessionStorage.getItem('gb-v2-gift-box');
      if (saved !== null) this.giftBoxChecked = saved === '1';
    } catch (e) { /* storage blocked: keep the default */ }
    document.querySelectorAll('.gb-gift-box-check').forEach(c => c.checked = this.giftBoxChecked);
    // Rev 15: a paid gift box shows its price in the shopper's currency (same format as the subtotal)
    document.querySelectorAll('[data-gb-gift-box-price]').forEach(el => { el.textContent = `+${this.formatMoney(this.giftBoxPrice)}`; });

    document.querySelectorAll('.gb-gift-box-check').forEach(cb => {
      cb.addEventListener('change', e => {
        this.giftBoxChecked = e.target.checked;
        document.querySelectorAll('.gb-gift-box-check').forEach(c => c.checked = this.giftBoxChecked);
        try { sessionStorage.setItem('gb-v2-gift-box', this.giftBoxChecked ? '1' : '0'); } catch (err) {}
        this.updateUI();                                         // subtotal (paid box) and the ready line both depend on it
      });
    });
  }

  /* ---- GIFT NOTE (desktop + mobile kept in sync; remembered across a refresh) ---- */
  bindGiftMessageEvents() {
    const boxes = [...document.querySelectorAll('[data-gb-giftnote]')];
    // Rev 17: the "Add a gift note" tick (sidebar + phone drawer) shows or hides the message field
    this.noteChecks = [...document.querySelectorAll('[data-gb-giftnote-check]')];

    // Bring back a note typed before a refresh; a saved note (or saved tick) re-ticks the box
    let ticked = false;
    try {
      this.giftNote = sessionStorage.getItem('gb-v2-gift-note') || '';
      const savedTick = sessionStorage.getItem('gb-v2-gift-note-on');
      ticked = savedTick !== null ? savedTick === '1' : this.giftNote !== '';      // older saves: a note means ticked
    } catch (e) { this.giftNote = ''; }
    if (this.giftNote) boxes.forEach(t => { t.value = this.giftNote; });   // restore a saved note only; never wipe what is already typed
    this.setNoteTicked(ticked, false);

    this.noteChecks.forEach(cb => {
      cb.addEventListener('change', e => {
        this.setNoteTicked(e.target.checked, true);
        try { sessionStorage.setItem('gb-v2-gift-note-on', e.target.checked ? '1' : '0'); } catch (err) {}
      });
    });

    boxes.forEach(textarea => {
      // 'input' covers typing and pasting; 'change' covers autofill/dictation that skip 'input'
      ['input', 'change'].forEach(evt => textarea.addEventListener(evt, e => {
        this.giftNote = e.target.value;
        boxes.forEach(t => { if (t !== e.target) t.value = this.giftNote; });
        try { sessionStorage.setItem('gb-v2-gift-note', this.giftNote); } catch (err) {}
      }));
    });
  }

  // Rev 17: ticks/unticks every gift note box and shows/hides its field; focuses the visible field when ticked by the shopper
  setNoteTicked(on, focus) {
    this.noteTicked = !!on;
    (this.noteChecks || []).forEach(c => { c.checked = this.noteTicked; });
    document.querySelectorAll('[data-gb-giftnote-field]').forEach(f => { f.hidden = !this.noteTicked; });
    if (on && focus) {
      const visible = [...document.querySelectorAll('[data-gb-giftnote-field] textarea')].find(t => t.offsetParent);
      if (visible) visible.focus();
    }
  }

  // The note to send: what is in a note box right now (falls back to the saved copy).
  // Rev 17: nothing is sent while "Add a gift note" is unticked (older markup without the tick: unchanged).
  currentGiftNote() {
    if (this.noteChecks && this.noteChecks.length && !this.noteTicked) return '';
    const typed = [...document.querySelectorAll('[data-gb-giftnote]')]
      .map(t => (t.value || '').trim())
      .find(v => v !== '');
    return typed || (this.giftNote || '').trim();
  }

  /* ---- MOBILE DRAWER (unchanged) ---- */
  bindMobileEvents() {
    document.getElementById('gb-mobile-open')?.addEventListener('click', () => this.openDrawer());
    document.getElementById('gb-drawer-close')?.addEventListener('click', () => this.closeDrawer());
    document.getElementById('gb-drawer-overlay')?.addEventListener('click', () => this.closeDrawer());
  }

  openDrawer() {
    const drawer = document.getElementById('gb-drawer');
    const overlay = document.getElementById('gb-drawer-overlay');
    drawer?.classList.add('gb-drawer--open');
    drawer?.setAttribute('aria-hidden', 'false');
    if (overlay) { overlay.style.display = 'block'; requestAnimationFrame(() => overlay.classList.add('gb-drawer-overlay--visible')); }
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    const drawer = document.getElementById('gb-drawer');
    const overlay = document.getElementById('gb-drawer-overlay');
    drawer?.classList.remove('gb-drawer--open');
    drawer?.setAttribute('aria-hidden', 'true');
    if (overlay) { overlay.classList.remove('gb-drawer-overlay--visible'); setTimeout(() => { overlay.style.display = ''; }, 300); }
    document.body.style.overflow = '';
  }

  /* ---- ADD GIFT TO CART: the ONLY moment anything goes into the cart ---- */
  bindSidebarEvents() {
    ['gb-add-to-cart', 'gb-drawer-add-to-cart'].forEach(id => {
      document.getElementById(id)?.addEventListener('click', () => this.addToCart());
    });
  }

  // Builds the list of cart lines for the one /cart/add.js request.
  // Rev 10: Shopify shows the newest line at the TOP of the cart, so a batch arrives upside down.
  // Lines are listed in gift-box order (each monogram fee right after its PJ, gift box last) and then
  // reversed before sending, so the cart reads in the same order as the gift box.
  buildCartLines() {
    const lines = [];
    this.heldItems.forEach(item => {
      if (item.monogram) {
        // Same property names real monogram orders carry, so fulfillment reads them the usual way.
        // "_gift_builder_ref" (hidden at checkout) pairs this PJ with its own $15 fee line below.
        const ref = `gb-${item.uid}`;
        // Rev 22: the monogram app's own link, copied from product-page orders: same number on the PJ and its fee
        // line, plus "<fee variant>-1" on the PJ. With it the app hides the fee line in the cart, keeps its
        // quantity in step with the PJ and removes it when the PJ is removed.
        const appRef = String(100000000 + Math.floor(Math.random() * 900000000));   // 9 digits, like the app's
        lines.push({
          id: parseInt(item.variantId),
          quantity: 1,
          properties: {
            'Single Line Text': item.monogram.text,
            'Choose Your Font Style': item.monogram.font,
            'I understand monogrammed items are Final Sale and take 7–10 business days to process.': 'Yes',
            LPROP: 'Final Sale',
            _gift_builder_ref: ref,
            ...(this.monogramAddonVariantId ? {                       // rev 22: only when a fee line goes with it
              _pc_pricing_ref: appRef,
              _pc_pricing_qty_split: `${this.monogramAddonVariantId}-1`,
            } : {}),
          },
          previewFor: item.uid,                     // rev 12: which preview picture goes on this line
        });
        if (this.monogramAddonVariantId) {
          lines.push({ id: parseInt(this.monogramAddonVariantId), quantity: 1, properties: { _gift_builder_ref: ref, _pc_pricing_ref: appRef } });   // rev 22: app link
        }
      } else {
        // Plain item: same "Color" property normal product-page adds carry
        lines.push({
          id: parseInt(item.variantId),
          quantity: 1,
          properties: item.color ? { Color: item.color } : {},
        });
      }
    });
    if (this.giftBoxChecked && this.giftBoxVariantId) {
      lines.push({ id: parseInt(this.giftBoxVariantId), quantity: 1 });
    }
    return lines.reverse();                       // rev 10: reversed so the cart shows them in gift-box order
  }

  /* Rev 19: reads the cart back and re-sends the gift note / gift box flag if either is missing.
     Waits briefly first so any other cart update fired by the add has finished. */
  async confirmCartGiftDetails(update) {
    const missing = cart => (update.attributes && (cart.attributes || {})['Gift Wrap'] !== 'yes')
      || (update.note && (cart.note || '') !== update.note);
    for (let attempt = 0; attempt < 2; attempt++) {
      await new Promise(r => setTimeout(r, 500));
      const cart = await fetch('/cart.js').then(r => r.json()).catch(() => null);
      if (!cart || !missing(cart)) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(update),
      }).catch(() => null);
    }
  }

  async addToCart() {
    const btns = ['gb-add-to-cart', 'gb-drawer-add-to-cart'].map(id => document.getElementById(id)).filter(Boolean);
    if (btns[0]?.disabled || !this.isReady()) return;          // rev 15: every step's rules must be met
    btns.forEach(b => { b.classList.add('gb-cta-btn--loading'); b.disabled = true; b.textContent = 'Adding...'; });

    try {
      // Rev 12: collect the monogram preview pictures (usually already finished in the background).
      // Waits at most 6 seconds; any picture not ready by then is skipped, never blocking the cart.
      const lines = this.buildCartLines();
      const previews = {};
      const monoLines = lines.filter(l => l.previewFor);
      if (monoLines.length) {
        const timeout = new Promise(resolve => setTimeout(resolve, 6000, 'timeout'));
        await Promise.race([
          Promise.all(monoLines.map(l => {
            const item = this.heldItems.find(i => i.uid === l.previewFor);
            return this.startPreview(item).then(blob => { if (blob) previews[l.previewFor] = blob; });
          })),
          timeout,
        ]);
      }

      // 0) Rev 19: gift note + gift box flag go on the cart FIRST, before the items.
      //    - note: the gift message, saved as the order note (what the cart's gift note box shows).
      //    - attributes: the cart's "Make it a gift" box (the Wrapped app) shows as TICKED only when
      //      these three cart attributes are set; the gift box product line alone leaves it unticked.
      //      Values copied from real orders where shoppers ticked that box (rev 8).
      //    Sent before AND after the add, then checked: in testing the attributes were once missing
      //    after an add (another script on the page updating the cart at the same moment).
      const update = {};
      const note = this.currentGiftNote();       // read live from the note boxes (rev 9)
      if (note) update.note = note;
      const wantBox = !!(this.giftBoxChecked && this.giftBoxVariantId);
      if (wantBox) {
        update.attributes = {
          'Gift Wrap': 'yes',
          'Gift Option': 'Entire Order',
          '_Gift Option ID': String(this.giftBoxVariantId),
        };
      }
      const sendUpdate = () => fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(update),
      }).catch(() => null);
      let before = null;                          // the cart's gift attributes before we touched them
      if (Object.keys(update).length) {
        before = await fetch('/cart.js').then(r => r.json()).then(c => c.attributes || {}).catch(() => ({}));
        await sendUpdate();
      }

      // 1) Everything in ONE request. With pictures it is sent as a form (files can't go in JSON);
      //    Shopify accepts the same items list either way and saves each picture's link as "Preview".
      let body;
      const headers = { 'Accept': 'application/json' };
      if (Object.keys(previews).length) {
        body = new FormData();
        lines.forEach((line, n) => {
          body.append(`items[${n}][id]`, String(line.id));
          body.append(`items[${n}][quantity]`, String(line.quantity));
          Object.entries(line.properties || {}).forEach(([key, value]) => body.append(`items[${n}][properties][${key}]`, value));
          if (previews[line.previewFor]) {
            body.append(`items[${n}][properties][Preview]`, previews[line.previewFor], 'monogram-preview.jpg');
            body.append(`items[${n}][properties][_pplr_preview]`, 'Preview');   // rev 22: the app's marker for its preview link
          }
        });
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ items: lines.map(({ previewFor, ...line }) => line) }); // previewFor is ours only
      }
      const res = await fetch('/cart/add.js', { method: 'POST', headers, body });
      if (!res.ok) {
        // Shopify explains the problem (e.g. a size that just sold out) in .description
        const body = await res.json().catch(() => ({}));
        // Rev 19: nothing was added, so put the cart's gift box flag back the way it was
        if (wantBox && before && before['Gift Wrap'] !== 'yes') {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: { 'Gift Wrap': '', 'Gift Option': '', '_Gift Option ID': '' } }),
          }).catch(() => null);
        }
        throw new Error(body.description || body.message || 'Something went wrong adding your gift to the cart.');
      }

      // 2) Same update again now the items are in, then check it stuck (one retry if not)
      if (Object.keys(update).length) {
        await sendUpdate();
        await this.confirmCartGiftDetails(update);
      }

      // 3) Done: note which discount codes this gift needs (rev 16), then clear the held box
      const wantedCodes = this.wantedDiscountCodes();
      this.heldItems = [];
      this.previewJobs = {};                      // rev 12: pictures are uploaded now; free the memory
      this.saveHeldItems();
      try { ['gb-v2-gift-note', 'gb-v2-gift-note-on', 'gb-v2-gift-box'].forEach(k => sessionStorage.removeItem(k)); } catch (e) {}

      btns.forEach(b => {
        b.classList.remove('gb-cta-btn--loading');
        b.classList.add('gb-cta-btn--success');
        b.textContent = 'Added to Cart!';
      });

      // 4) Rev 16: put the codes for the free extras on the cart (and drop stale ones), then go to the cart
      await this.applyDiscountCodes(wantedCodes);
      const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      setTimeout(() => { window.location.href = cartUrl; }, 600);

    } catch (err) {
      btns.forEach(b => {
        b.classList.remove('gb-cta-btn--loading');
        b.disabled = false;
        b.textContent = 'Add Gift to Cart';
      });
      this.setCtaError(err.message || 'Something went wrong. Please try again.');
      console.error('Gift builder cart error:', err);
    }
  }

  /* Rev 16: the codes this gift needs: the gift box's code when it is free and ticked, the monogram code
     when monogramming is free and a monogram is in the gift. Same code twice counts once. */
  wantedDiscountCodes() {
    const codes = [];
    if (this.giftBoxFree && this.giftBoxChecked && this.giftBoxVariantId && this.giftBoxCode) codes.push(this.giftBoxCode);
    if (this.monogramFree && this.heldItems.some(i => i.monogram) && this.monogramCode) codes.push(this.monogramCode);
    return codes.filter((c, i) => codes.findIndex(x => x.toLowerCase() === c.toLowerCase()) === i);
  }

  /* Rev 16: sets the cart's discount codes to: the shopper's own codes + the codes this gift needs.
     This page's codes that are not needed now (e.g. left from an earlier visit) are removed, so a
     paid extra never rings up free. Uses the cart's own list, so two different codes can both apply. */
  async applyDiscountCodes(wanted = this.wantedDiscountCodes()) {
    try {
      // This page's codes, plus ComplimentaryGift (covers both extras) in case the settings moved away from it
      const ours = [this.giftBoxCode, this.monogramCode, 'ComplimentaryGift'].filter(Boolean).map(c => c.toLowerCase());
      const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
      const current = (cart.discount_codes || []).map(dc => dc.code);
      const lc = list => list.map(c => c.toLowerCase());
      const keepShopper = current.filter(c => !ours.includes(c.toLowerCase()));          // the shopper's own codes stay
      const next = keepShopper.concat(wanted.filter(c => !lc(keepShopper).includes(c.toLowerCase())));
      if (lc(next).join(',') !== lc(current).join(',')) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: next.join(',') }),
        });
      }
      // An old /discount/ link leaves a browser cookie with the code; clear it when it is ours and not needed
      const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
      const cookieCode = m ? decodeURIComponent(m[1]).toLowerCase() : '';
      if (cookieCode && ours.includes(cookieCode) && !lc(wanted).includes(cookieCode)) document.cookie = 'discount_code=; Max-Age=0; path=/';
    } catch (e) {
      console.warn('Gift builder: could not update discount codes', e);   // never blocks the cart
    }
  }

  // Shows an error under the button; the next change to the box replaces it with normal text
  setCtaError(message) {
    ['gb-cta-note', 'gb-drawer-note'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.textContent = message;
        el.classList.add('gb-cta-note--error');
      }
    });
  }

  // Formats cents using the store's own money format (same as before)
  formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const defaultOption = (opt, def) => (typeof opt === 'undefined' || opt === null ? def : opt);

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const match = format.match(placeholderRegex);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    let out = format.replace(placeholderRegex, value);
    // Rev 11: outside the US, "$" can mean CAD, AUD, etc. Add the currency code ("$300.00 CAD") so it is
    // never mistaken for US dollars. £, € and other symbols are already unambiguous and stay as they are.
    const code = window.theme && window.theme.currencyCode;
    const shopCode = window.theme && window.theme.shopCurrency;
    if (code && shopCode && code !== shopCode && out.trim().startsWith('$')) out += ` ${code}`;
    return out;
  }
}

document.addEventListener('DOMContentLoaded', () => new GiftBuilderV2());
